<template>
  <div
    v-if="isVisible"
    :id="`banner-${uuid}`"
    v-editable="blok"
    :data-testid="`banner-${uuid}`"
    class="relative text-sm"
    :role="isLink ? 'link' : 'banner'"
    :class="{
      'cursor-pointer': isLink,
      'cursor-default': !isLink,
    }"
    @click="onClick"
  >
    <slot :close="close">
      <FadeInTransition :duration="300">
        <div
          class="relative z-50 mx-auto flex w-full items-center border-b border-gray-300 py-3"
          :style="{ background, color }"
        >
          <section
            class="!md:container mx-auto flex flex-col flex-wrap items-center justify-center gap-2 text-pretty pl-10 text-center md:relative md:flex-row md:gap-4"
            :class="blok.closeButton ? 'pr-14' : 'pr-10'"
          >
            <slot name="body">
              <template v-for="item in blok.items">
                <BannerLink
                  v-if="item.component === 'BannerLink'"
                  :key="item._uid"
                  :data-testid="`banner_button_${item._uid}`"
                  :link="item"
                  :fallback-tracking-data="trackingData"
                  :target="linkTarget"
                  :theme="blok.theme || 'light'"
                />
                <BannerText
                  v-if="item.component === 'BannerText'"
                  :key="item._uid"
                  :blok="item"
                />
                <FimCountdown
                  v-if="item.component === 'Countdown' && item.countdownUntil"
                  :key="item._uid"
                  :blok="item"
                />
              </template>
            </slot>
          </section>
          <slot v-if="blok.closeButton" name="action" :close="close">
            <div
              data-testid="banner_button_close"
              class="absolute right-5 top-6 md:top-auto"
              @click.prevent.stop="close(uuid)"
            >
              <LazyIconFielmannClose class="size-6" />
            </div>
          </slot>
        </div>
      </FadeInTransition>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type {
  SbGlobalBanner,
  StoryblokTrackingContent,
} from '~/storyblok/types/storyblok'
import { ctaURLHandler } from '~/storyblok/utils/links'
import { isStoryblokTrackingContent } from '~/utils/typeguards'

const props = defineProps({
  blok: {
    type: Object as PropType<SbGlobalBanner & { published_at: string }>,
    required: true,
  },
  uuid: {
    type: String as PropType<string>,
    default: '',
  },
})

const $currentShop = useCurrentShop()
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { close, shouldBeVisible } = await useBanners()
const { trackPromotionView, trackPromotionClick } = await useTrackingEvents()

const trackingData = computed<StoryblokTrackingContent>(() => {
  return props.blok as StoryblokTrackingContent
})

const isLink = computed(() => !!props?.blok?.cta_url?.cached_url)
const cachedUrl = computed(() => ctaURLHandler(props.blok, $currentShop.value))
const linkTarget = props.blok.cta_target_blank ? '_blank' : '_self'

const onClick = async () => {
  if (!isLink.value) {
    return
  }

  if (isStoryblokTrackingContent(trackingData.value)) {
    trackPromotionClick(trackingData.value)
  }

  if (props.blok?.cta_url?.linktype === 'story') {
    return await navigateTo(cachedUrl.value, {
      open: {
        target: linkTarget,
      },
    })
  } else {
    window.open(cachedUrl.value, linkTarget)
  }
}

const isVisible = computed(() => shouldBeVisible(props.blok, props.uuid))
const background = computed(() => props.blok.background_color?.color)
const color = computed(() => props.blok.text_color?.color)

scope?.run(() => {
  if (import.meta.server) {
    return
  }
  watch(
    isVisible,
    (newValue) => {
      if (newValue && isStoryblokTrackingContent(props.blok)) {
        trackPromotionView(props.blok)
      }
    },
    { immediate: true },
  )
})
</script>
